import React from 'react';

/**
 * Route-level error boundary.
 *
 * Catches render-time errors in any child component and renders a
 * clear recovery surface instead of letting the whole app go blank.
 * The full stack is kept behind a <details> so it's available for a
 * paste into a bug report without shouting at the user by default.
 *
 * We use a class because React still requires it for
 * componentDidCatch / getDerivedStateFromError — there's no hook
 * equivalent as of React 19.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error, info: null };
  }

  componentDidCatch(error, info) {
    this.setState({ error, info });
    // eslint-disable-next-line no-console
    console.error('[spotlight-dashboard] caught render error:', error, info);
  }

  reset = () => this.setState({ error: null, info: null });

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    // Strip the leak in prod.  Stack traces, exception messages
    // (which often include URLs, secrets, internal field names) get
    // swapped for a generic "Something broke" surface.  Devs still
    // see the same console.error from componentDidCatch above, and
    // the same info ends up in any Sentry/OTel error reporter the
    // build wires in later.
    const showStack = import.meta.env.DEV;

    return (
      <div className="page">
        <div className="page__title">
          <div>
            <h1>Something broke on this page</h1>
            <p className="page__subtitle">
              {showStack
                ? 'The error is contained to this route — the rest of the dashboard still works. Try reloading; if it repeats, the stack below is what we need to fix it.'
                : "The error is contained to this route. Try reloading; if it repeats, refresh or contact support — the issue's been logged on our side."}
            </p>
          </div>
        </div>

        {showStack && (
          <div className="state state--error">
            <div className="state__title">{error?.message || String(error)}</div>
          </div>
        )}

        <div className="row">
          <button type="button" className="btn btn--primary" onClick={this.reset}>
            Retry
          </button>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
        </div>

        {showStack && (
          <details style={{ marginTop: 12 }}>
            <summary style={{ cursor: 'pointer', color: 'var(--text-3)' }}>
              Stack trace (dev build only)
            </summary>
            <pre className="error-stack">
              {error?.stack || String(error)}
              {info?.componentStack ? `\n\nComponent stack:${info.componentStack}` : ''}
            </pre>
          </details>
        )}

        <style>{`
          .error-stack {
            background: var(--text-1);
            color: var(--surface-muted);
            padding: 12px;
            border-radius: var(--r-md);
            white-space: pre-wrap;
            word-break: break-all;
            font-size: 11.5px;
            margin-top: 10px;
            max-height: 420px;
            overflow: auto;
          }
        `}</style>
      </div>
    );
  }
}
